import { casinoIdListEnum } from 'common/commonEnum';
import { heatLegendAllInsight } from 'common/common';
import { floormapHeatBeanRatings, heatBeanRatings } from 'config/HeatBeanRatings';
import mapboxgl from 'mapbox-gl';

// Shared layer builder for AutoCAD-sourced casinos (RWC, SSC, ACH, JJC).
// Adds a line layer (to outline amenity blocks) and a symbol layer (for labels).
const addAutocadBankIdLayers = (
  map: mapboxgl.Map,
  suffix: string,
  lineBlockNames: string[]
) => {
  const lineWidthCase: mapboxgl.Expression = [
    'case',
    ...lineBlockNames.flatMap((name) => [['==', ['get', 'autocad_block_name'], name], 1] as mapboxgl.Expression[]),
    0,
  ] as mapboxgl.Expression;

  map.addLayer({
    id: `bankIdLayer${suffix}`,
    type: 'line',
    source: 'bankIdSource',
    layout: {},
    paint: { 'line-color': '#fff', 'line-width': lineWidthCase },
  });

  map.addLayer({
    id: `bankIdLabelLayer${suffix}`,
    type: 'symbol',
    source: 'bankIdSource',
    layout: {
      'text-field': ['to-string', ['get', 'autocad_text_string']],
      'text-padding': 0,
      'text-font': ['Open Sans Bold', 'Arial Unicode MS Regular'],
      'text-offset': [0, 0],
      'text-anchor': 'center',
      'text-justify': 'center',
      'text-size': ['interpolate', ['linear'], ['zoom'], 13, 2, 14, 2, 15, 4, 16, 9, 17, 20],
      'text-rotation-alignment': 'map',
      'text-rotate': ['get', 'autocad_rotation'],
    },
    paint: { 'text-color': '#f7f5f7', 'text-opacity': 1 },
  });
};

// Add a new AutoCAD casino here — no other code changes needed.
const AUTOCAD_CASINO_LAYERS: Partial<Record<casinoIdListEnum, { suffix: string; blockNames: string[] }>> = {
  [casinoIdListEnum.RWC]: {
    suffix: 'RWC',
    blockNames: ['ATM', 'Kiosk', 'Cashier', 'Sports_Kiosk', 'Keno_Kiosk', 'Cashier_walkway', 'Check', 'MKTG', 'Enroll'],
  },
  [casinoIdListEnum.SSC]: { suffix: 'SSC', blockNames: ['ATM', 'KIOSK', 'Kiosk', 'PROMO'] },
  [casinoIdListEnum.ACH]: { suffix: 'ACH', blockNames: ['ATM', 'KIOSK', 'Kiosk', 'PROMO'] },
  [casinoIdListEnum.JJC]: { suffix: 'JJC', blockNames: ['ATM', 'KIOSK', 'Kiosk', 'PROMO'] },
};

export const setupBankIdLayers = (map: mapboxgl.Map, casinoId: number) => {
  const autocad = AUTOCAD_CASINO_LAYERS[casinoId as casinoIdListEnum];
  if (autocad) {
    addAutocadBankIdLayers(map, autocad.suffix, autocad.blockNames);
    return;
  }

  if (casinoId === casinoIdListEnum.LOTC || casinoId === casinoIdListEnum.EUREKA) {
    map.addLayer({
      id: 'bankIdLayer',
      type: 'symbol',
      source: 'bankIdSource',
      layout: {
        'text-field': ['to-string', ['get', 'name']],
        'text-size': ['interpolate', ['linear'], ['zoom'], 16, 10, 17, 20, 18, 30, 19, 45, 20, 60, 24, 75],
        'text-justify': 'left',
        'text-anchor': 'left',
        'text-padding': 0,
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Regular'],
      },
      paint: { 'text-color': '#f7f5f7', 'text-opacity': 1 },
    });
  }
};

export const setupAddWallLayer = (map: mapboxgl.Map) => {
  map.addLayer({
    id: 'wallLayer',
    type: 'line',
    source: 'wallSource',
    layout: {},
    paint: {
      'line-color': [
        'match',
        ['get', 'autocad_block_name'],
        'Poker_Table',
        heatBeanRatings[0].color,
        'Roulette_Table',
        heatBeanRatings[0].color,
        'Craps_Table',
        heatBeanRatings[0].color,
        'Sports_Bedding',
        heatBeanRatings[0].color,
        '#fff',
      ],
      'line-width': 1.2,
    },
  });
};

export const setupSlotTextLayer = (map: mapboxgl.Map) => {
  const hasThemeExpression = ['!=', ['coalesce', ['get', 'game_theme'], ''], ''];
  const isLongTitleExpression = ['>', ['length', ['coalesce', ['get', 'game_title'], '']], 18];

  map.addLayer({
    id: 'slotNumberLayer',
    type: 'symbol',
    source: 'slotSource',
    minzoom: 16,
    layout: {
      'text-field': ['to-string', ['get', 'game_slot_number']],
      'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
      'text-allow-overlap': true,
      'text-ignore-placement': true,
      'text-anchor': 'top',
      'text-justify': 'center',
      'text-offset': [0, -3.5],
      'text-padding': 1,
      'text-size': [
        'interpolate',
        ['linear'],
        ['zoom'],
        16,
        2.64,
        17,
        4.08,
        18,
        7.44,
        18.5,
        8.16,
        19,
        15.84,
        20,
        22.8,
        24,
        45.6,
      ],
    },
  });

  map.addLayer({
    id: 'gameNameLayer',
    type: 'symbol',
    source: 'slotSource',
    minzoom: 16,
    layout: {
      'text-field': ['to-string', ['get', 'game_title']],
      'text-font': ['Open Sans Bold', 'Arial Unicode MS Regular'],
      'text-allow-overlap': true,
      'text-ignore-placement': true,
      'text-anchor': 'center',
      'text-justify': 'center',
      'text-max-width': ['case', isLongTitleExpression, 6, 8],
      'text-offset': [
        'case',
        hasThemeExpression,
        ['literal', [0, -0.6]],
        ['literal', [0, -0.2]],
      ],
      'text-padding': 3,
      'text-size': [
        'interpolate',
        ['linear'],
        ['zoom'],
        16,
        3.12,
        17,
        5.04,
        18,
        9.12,
        18.5,
        10.08,
        19,
        19.2,
        20,
        26.4,
        24,
        52.8,
      ],
    },
  });

  map.addLayer({
    id: 'gameThemeLayer',
    type: 'symbol',
    source: 'slotSource',
    minzoom: 16,
    layout: {
      'text-field': ['to-string', ['get', 'game_theme']],
      'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
      'text-allow-overlap': true,
      'text-ignore-placement': true,
      'text-anchor': 'center',
      'text-justify': 'center',
      'text-offset': [0, 1.3],
      'text-padding': 1,
      'text-size': [
        'interpolate',
        ['linear'],
        ['zoom'],
        16,
        2.64,
        17,
        4.32,
        18,
        7.68,
        18.5,
        8.4,
        19,
        16.08,
        20,
        22.8,
        24,
        45.6,
      ],
    },
  });

  map.addLayer({
    id: 'insightLayer',
    type: 'symbol',
    source: 'slotSource',
    minzoom: 16,
    layout: {
      'text-field': ['to-string', ['get', 'game_insight']],
      'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
      'text-allow-overlap': true,
      'text-ignore-placement': true,
      'text-anchor': 'center',
      'text-justify': 'center',
      'text-offset': [
        'case',
        hasThemeExpression,
        [
          'case',
          isLongTitleExpression,
          ['literal', [0, 3.4]],
          ['literal', [0, 2.9]],
        ],
        [
          'case',
          isLongTitleExpression,
          ['literal', [0, 2.3]],
          ['literal', [0, 1.7]],
        ],
      ],
      'text-padding': 1,
      'text-size': [
        'interpolate',
        ['linear'],
        ['zoom'],
        16,
        2.64,
        17,
        4.32,
        18,
        7.68,
        18.5,
        8.4,
        19,
        16.08,
        20,
        22.8,
        24,
        45.6,
      ],
    },
  });
};

export const setupBorderLayer = (map: mapboxgl.Map) => {
  map.addLayer({
    id: 'slotBorderLayer',
    type: 'line',
    source: 'slotSource',
    layout: {},
    paint: { 'line-color': 'hsl(222, 8%, 23%)', 'line-width': 1 },
  });
};

export const setupHeatLayer = (map: mapboxgl.Map) => {
  const fillColorExpression: (string | boolean | number | unknown[])[] = ['case'];

  heatLegendAllInsight.forEach((heatLevel) => {
    fillColorExpression.push(['match', ['feature-state', 'heat'], [heatLevel], true, false]);
    fillColorExpression.push(floormapHeatBeanRatings[heatLevel].color);
  });

  fillColorExpression.push(floormapHeatBeanRatings[0].color);

  map.addLayer({
    id: 'slotLayer',
    type: 'fill',
    source: 'slotSource',
    layout: {},
    paint: {
      'fill-color': fillColorExpression as unknown as mapboxgl.Expression,
      'fill-outline-color': '#000000',
    },
  });
};
